import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import TimeExtent from '@arcgis/core/time/TimeExtent';
import { useUscgMapContext } from '../UscgMapContext';

const MAX_STEPS = 168; // up to ~1 week hourly
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const POLL_MS = 200;
const MAX_POLLS = 20;

export type ExternalLayerTimeSteps = {
  steps: Date[];
  stepIndex: number;
  setStepIndex: (index: number) => void;
  setStepFromDate: (date: Date) => void;
  fullStart: Date | undefined;
  fullEnd: Date | undefined;
  status: 'loading' | 'ready' | 'unavailable';
  message?: string;
};

/** Recursively collect FeatureLayers from GroupLayer / MapImageLayer trees. */
export function collectFeatureLayers(
  layers: __esri.Layer[] | undefined
): FeatureLayer[] {
  if (!layers?.length) return [];
  const out: FeatureLayer[] = [];

  const visit = (layer: __esri.Layer) => {
    if (layer instanceof FeatureLayer) {
      out.push(layer);
      return;
    }
    if (layer instanceof GroupLayer) {
      for (const child of layer.allLayers.toArray()) {
        visit(child);
      }
      return;
    }
    if (layer instanceof MapImageLayer) {
      // Sublayers are not FeatureLayers; skip for timeExtent.
      return;
    }
    // WebMap / other containers may expose a layers collection
    const maybeLayers = (layer as { layers?: __esri.Collection<__esri.Layer> })
      .layers;
    if (maybeLayers && typeof maybeLayers.toArray === 'function') {
      for (const child of maybeLayers.toArray()) {
        visit(child);
      }
    }
  };

  for (const layer of layers) {
    visit(layer);
  }
  return out;
}

function intervalToMs(
  interval: __esri.TimeInterval | null | undefined
): number | null {
  if (!interval || typeof interval.value !== 'number' || interval.value <= 0) {
    return null;
  }
  const unit = String(interval.unit || 'hours').toLowerCase();
  switch (unit) {
    case 'milliseconds':
      return interval.value;
    case 'seconds':
      return interval.value * 1000;
    case 'minutes':
      return interval.value * 60 * 1000;
    case 'hours':
      return interval.value * HOUR_MS;
    case 'days':
      return interval.value * DAY_MS;
    case 'weeks':
      return interval.value * 7 * DAY_MS;
    case 'months':
      return interval.value * 30 * DAY_MS;
    case 'years':
      return interval.value * 365 * DAY_MS;
    default:
      return interval.value * HOUR_MS;
  }
}

function buildSteps(start: Date, end: Date, intervalMs: number): Date[] {
  const steps: Date[] = [];
  let t = start.getTime();
  const endMs = end.getTime();
  if (!(t < endMs) || !Number.isFinite(t) || !Number.isFinite(endMs)) {
    return [];
  }
  while (t <= endMs && steps.length < MAX_STEPS) {
    steps.push(new Date(t));
    t += intervalMs;
  }
  if (steps.length === 0) {
    steps.push(new Date(start));
  }
  const last = steps[steps.length - 1];
  if (last && last.getTime() < endMs && steps.length < MAX_STEPS) {
    steps.push(new Date(end));
  }
  return steps;
}

function pickDateField(
  layer: FeatureLayer,
  preferredDateField?: string | null
): string | null {
  const fields = layer.fields ?? [];
  if (preferredDateField) {
    const preferred = fields.find(
      (f) => f.name?.toLowerCase() === preferredDateField.toLowerCase()
    );
    if (preferred?.name) return preferred.name;
  }
  const preferredNames = [
    'datetime',
    'date_time',
    'event_date',
    'observation_date',
    'last_edited_date',
    'editdate',
    'date',
  ];
  const byName = preferredNames
    .map((name) =>
      fields.find((f) => f.name?.toLowerCase() === name && f.type === 'date')
    )
    .find(Boolean);
  if (byName?.name) return byName.name;

  const anyDate = fields.find((f) => f.type === 'date');
  return anyDate?.name ?? null;
}

function formatSqlDate(date: Date): string {
  const iso = date.toISOString();
  return iso.slice(0, 19).replace('T', ' ');
}

function formatTimeFilterLabel(date: Date): string {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Human-readable start–stop label for AI chips and shared control. */
export function formatTimeRangeLabel(start: Date, end: Date): string {
  return `${formatTimeFilterLabel(start)} – ${formatTimeFilterLabel(end)}`;
}

export type LayerTimeApplyMode = 'timeExtent' | 'definitionExpression';

export type DiscoverLayerTimeBoundsResult =
  | {
      ok: true;
      fullStart: Date;
      fullEnd: Date;
      mode: LayerTimeApplyMode;
      dateField: string | null;
    }
  | { ok: false; message: string };

/**
 * Discover the full time bounds and apply mode for a set of FeatureLayers
 * belonging to one external layer id.
 */
export async function discoverLayerTimeBounds(
  featureLayers: FeatureLayer[],
  preferredDateField?: string | null
): Promise<DiscoverLayerTimeBoundsResult> {
  if (featureLayers.length === 0) {
    return { ok: false, message: 'Layer is not loaded on the map yet.' };
  }

  await Promise.all(
    featureLayers.map((fl) => fl.load().catch(() => undefined))
  );
  const primary = featureLayers[0];

  if (!preferredDateField) {
    const timeInfo = primary.timeInfo;
    const full = timeInfo?.fullTimeExtent ?? null;
    const start = full?.start ? new Date(full.start) : null;
    const end = full?.end ? new Date(full.end) : null;
    if (
      start &&
      end &&
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      start.getTime() <= end.getTime()
    ) {
      return {
        ok: true,
        fullStart: start,
        fullEnd: end,
        mode: 'timeExtent',
        dateField: null,
      };
    }
  }

  const field = pickDateField(primary, preferredDateField);
  if (!field) {
    return { ok: false, message: 'Time info unavailable for this layer.' };
  }

  const result = await discoverDefinitionExpressionSteps(primary, field);
  if (!result.ok) {
    return { ok: false, message: result.message };
  }
  const fullStart = result.steps[0];
  const fullEnd = result.steps[result.steps.length - 1];
  if (!fullStart || !fullEnd) {
    return { ok: false, message: 'No dated features found on this layer.' };
  }
  return {
    ok: true,
    fullStart,
    fullEnd,
    mode: 'definitionExpression',
    dateField: result.field,
  };
}

export type ApplyExternalLayerTimeWindowParams = {
  layerId: string;
  featureLayers: FeatureLayer[];
  mode: LayerTimeApplyMode;
  dateField: string | null;
  start: Date;
  end: Date;
  setExternalLayerTimeFilter: (
    layerId: string,
    filter: {
      startIso: string;
      endIso: string;
      label: string;
    } | null
  ) => void;
  setLayerDefinitionExpressionTimePart: (
    layerId: string,
    expression: string | null
  ) => void;
};

/** Apply an explicit [start, end] window to one external layer's FeatureLayers. */
export function applyExternalLayerTimeWindow({
  layerId,
  featureLayers,
  mode,
  dateField,
  start,
  end,
  setExternalLayerTimeFilter,
  setLayerDefinitionExpressionTimePart,
}: ApplyExternalLayerTimeWindowParams): void {
  if (!featureLayers.length) return;

  const rangeStart = start.getTime() <= end.getTime() ? start : end;
  const rangeEnd = start.getTime() <= end.getTime() ? end : start;

  for (const fl of featureLayers) {
    if (mode === 'timeExtent') {
      fl.timeExtent = new TimeExtent({
        start: rangeStart,
        end: rangeEnd,
      });
    }
  }

  if (mode === 'definitionExpression' && dateField) {
    const startSql = formatSqlDate(rangeStart);
    const endSql = formatSqlDate(rangeEnd);
    setLayerDefinitionExpressionTimePart(
      layerId,
      `${dateField} >= DATE '${startSql}' AND ${dateField} <= DATE '${endSql}'`
    );
  } else {
    setLayerDefinitionExpressionTimePart(layerId, null);
  }

  setExternalLayerTimeFilter(layerId, {
    startIso: rangeStart.toISOString(),
    endIso: rangeEnd.toISOString(),
    label: formatTimeRangeLabel(rangeStart, rangeEnd),
  });
}

function nearestStepIndex(steps: Date[], date: Date): number {
  if (steps.length === 0) return 0;
  const target = date.getTime();
  let best = 0;
  let bestDelta = Math.abs(steps[0].getTime() - target);
  for (let i = 1; i < steps.length; i++) {
    const delta = Math.abs(steps[i].getTime() - target);
    if (delta < bestDelta) {
      best = i;
      bestDelta = delta;
    }
  }
  return best;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseAttrDate(raw: unknown): number | null {
  const ms =
    typeof raw === 'number'
      ? raw
      : raw instanceof Date
        ? raw.getTime()
        : Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : null;
}

async function discoverDefinitionExpressionSteps(
  primary: FeatureLayer,
  field: string
): Promise<
  | { ok: true; steps: Date[]; intervalMs: number; field: string }
  | { ok: false; message: string }
> {
  try {
    // Prefer distinct values so a single page of features with one repeated
    // date doesn't collapse the slider to a single non-draggable step.
    let uniqueTimes = new Set<number>();
    try {
      const distinctResult = await primary.queryFeatures({
        where: '1=1',
        outFields: [field],
        returnGeometry: false,
        returnDistinctValues: true,
        orderByFields: [`${field} ASC`],
        num: MAX_STEPS,
      });
      for (const g of distinctResult.features) {
        const ms = parseAttrDate(g.attributes?.[field]);
        if (ms != null) uniqueTimes.add(ms);
      }
    } catch {
      uniqueTimes = new Set();
    }

    if (uniqueTimes.size < 2) {
      // Fall back to min/max statistics and synthesize a continuous range.
      try {
        const statsResult = await primary.queryFeatures({
          where: '1=1',
          outStatistics: [
            {
              onStatisticField: field,
              outStatisticFieldName: 'min_date',
              statisticType: 'min',
            },
            {
              onStatisticField: field,
              outStatisticFieldName: 'max_date',
              statisticType: 'max',
            },
          ] as __esri.StatisticDefinition[],
        });
        const attrs = statsResult.features[0]?.attributes ?? {};
        const minMs = parseAttrDate(attrs.min_date);
        const maxMs = parseAttrDate(attrs.max_date);
        if (minMs != null && maxMs != null && minMs <= maxMs) {
          const span = maxMs - minMs;
          let intervalMs = span / HOUR_MS <= MAX_STEPS ? HOUR_MS : DAY_MS;
          if (span / intervalMs > MAX_STEPS) {
            intervalMs = Math.max(1, Math.ceil(span / MAX_STEPS));
          }
          if (span === 0) {
            return {
              ok: true,
              steps: [new Date(minMs)],
              intervalMs: HOUR_MS,
              field,
            };
          }
          const built = buildSteps(
            new Date(minMs),
            new Date(maxMs),
            intervalMs
          );
          return { ok: true, steps: built, intervalMs, field };
        }
      } catch {
        // fall through to sampled unique times
      }
    }

    if (uniqueTimes.size === 0) {
      const sampleResult = await primary.queryFeatures({
        where: '1=1',
        outFields: [field],
        returnGeometry: false,
        orderByFields: [`${field} ASC`],
        num: MAX_STEPS,
      });
      for (const g of sampleResult.features) {
        const ms = parseAttrDate(g.attributes?.[field]);
        if (ms != null) uniqueTimes.add(ms);
      }
    }

    const nextSteps = Array.from(uniqueTimes)
      .sort((a, b) => a - b)
      .map((ms) => new Date(ms));
    if (nextSteps.length === 0) {
      return { ok: false, message: 'No dated features found on this layer.' };
    }
    let inferredInterval = HOUR_MS;
    if (nextSteps.length >= 2) {
      inferredInterval = Math.max(
        1,
        nextSteps[1].getTime() - nextSteps[0].getTime()
      );
    }
    return {
      ok: true,
      steps: nextSteps,
      intervalMs: inferredInterval,
      field,
    };
  } catch {
    return { ok: false, message: 'Time info unavailable for this layer.' };
  }
}

export type UseExternalLayerTimeExtentOptions = {
  /**
   * Prefer this date field for definitionExpression filtering (e.g.
   * capture_date).
   */
  preferredDateField?: string | null;
  /** Restore slider position from a previously persisted selection (ISO start). */
  initialTimeIso?: string | null;
};

/**
 * Discover time steps for an external layer and apply FeatureLayer.timeExtent
 * (or definitionExpression fallback) for the selected single datetime step.
 */
export function useExternalLayerTimeExtent(
  externalLayerId: string,
  options?: UseExternalLayerTimeExtentOptions
): ExternalLayerTimeSteps {
  const preferredDateField = options?.preferredDateField ?? null;
  const initialTimeIso = options?.initialTimeIso ?? null;
  const {
    getAddedExternalLayers,
    isExternalLayersReady,
    isExternalLayerVisible,
    externalLayerTimeFilterById,
    setExternalLayerTimeFilter,
    setLayerDefinitionExpressionTimePart,
  } = useUscgMapContext();
  const layerId = String(externalLayerId);
  const isVisible = isExternalLayerVisible(layerId);
  const [steps, setSteps] = useState<Date[]>([]);
  const [stepIndex, setStepIndexState] = useState(0);
  const [intervalMs, setIntervalMs] = useState(HOUR_MS);
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>(
    'loading'
  );
  const [message, setMessage] = useState<string | undefined>();
  const [mode, setMode] = useState<'timeExtent' | 'definitionExpression'>(
    'timeExtent'
  );
  const [dateField, setDateField] = useState<string | null>(null);
  // Snapshot only for discovery restore — do not re-discover when the user
  // drags the slider (that would reset mid-interaction).
  const restoreIsoRef = useRef<string | null>(
    externalLayerTimeFilterById[layerId]?.startIso || initialTimeIso || null
  );
  restoreIsoRef.current =
    externalLayerTimeFilterById[layerId]?.startIso || initialTimeIso || null;

  useEffect(() => {
    let cancelled = false;

    const resolveInitialStepIndex = (nextSteps: Date[]) => {
      const restoreIso = restoreIsoRef.current;
      if (!restoreIso || nextSteps.length === 0) return 0;
      const parsed = new Date(restoreIso);
      if (Number.isNaN(parsed.getTime())) return 0;
      return nearestStepIndex(nextSteps, parsed);
    };

    const waitForFeatureLayers = async (): Promise<FeatureLayer[]> => {
      for (let i = 0; i < MAX_POLLS; i++) {
        if (cancelled) return [];
        const layers = getAddedExternalLayers(layerId);
        const featureLayers = collectFeatureLayers(layers);
        if (featureLayers.length > 0) return featureLayers;
        await sleep(POLL_MS);
      }
      return collectFeatureLayers(getAddedExternalLayers(layerId));
    };

    const discover = async () => {
      setStatus('loading');
      setMessage(undefined);

      if (!isExternalLayersReady) {
        // Keep loading until sync reports ready; effect re-runs on ready flip.
        return;
      }

      const featureLayers = await waitForFeatureLayers();
      if (cancelled) return;

      if (featureLayers.length === 0) {
        setSteps([]);
        setStatus('unavailable');
        setMessage('Layer is not loaded on the map yet.');
        return;
      }

      await Promise.all(
        featureLayers.map((fl) => fl.load().catch(() => undefined))
      );
      if (cancelled) return;

      const primary = featureLayers[0];

      // When a preferred attribute field is set (e.g. FloodingBins capture_date),
      // skip layer timeInfo and filter via definitionExpression on that field.
      if (!preferredDateField) {
        const timeInfo = primary.timeInfo;
        const full = timeInfo?.fullTimeExtent ?? null;

        const start = full?.start ? new Date(full.start) : null;
        const end = full?.end ? new Date(full.end) : null;

        if (
          start &&
          end &&
          !Number.isNaN(start.getTime()) &&
          !Number.isNaN(end.getTime())
        ) {
          const fromInterval = intervalToMs(timeInfo?.interval);
          const span = end.getTime() - start.getTime();
          let nextIntervalMs = fromInterval;
          if (!nextIntervalMs || span / nextIntervalMs > MAX_STEPS) {
            nextIntervalMs = span / HOUR_MS <= MAX_STEPS ? HOUR_MS : DAY_MS;
            if (span / nextIntervalMs > MAX_STEPS) {
              nextIntervalMs = Math.ceil(span / MAX_STEPS);
            }
          }
          const nextSteps = buildSteps(start, end, nextIntervalMs);
          if (nextSteps.length > 0) {
            setMode('timeExtent');
            setDateField(null);
            setIntervalMs(nextIntervalMs);
            setSteps(nextSteps);
            setStepIndexState(resolveInitialStepIndex(nextSteps));
            setStatus('ready');
            return;
          }
        }
      }

      const field = pickDateField(primary, preferredDateField);
      if (!field) {
        setSteps([]);
        setStatus('unavailable');
        setMessage('Time info unavailable for this layer.');
        return;
      }

      const result = await discoverDefinitionExpressionSteps(primary, field);
      if (cancelled) return;
      if (!result.ok) {
        setSteps([]);
        setStatus('unavailable');
        setMessage(result.message);
        return;
      }
      setMode('definitionExpression');
      setDateField(result.field);
      setIntervalMs(result.intervalMs);
      setSteps(result.steps);
      setStepIndexState(resolveInitialStepIndex(result.steps));
      setStatus('ready');
    };

    void discover();
    return () => {
      cancelled = true;
    };
  }, [
    layerId,
    getAddedExternalLayers,
    isExternalLayersReady,
    isVisible,
    preferredDateField,
  ]);

  const applyStep = useCallback(
    (index: number, nextSteps: Date[]) => {
      const layers = getAddedExternalLayers(layerId);
      const featureLayers = collectFeatureLayers(layers);
      if (!featureLayers.length || !nextSteps.length) return;

      const clamped = Math.max(0, Math.min(index, nextSteps.length - 1));
      const start = nextSteps[clamped];
      const next = nextSteps[clamped + 1];
      const end = next
        ? new Date(next.getTime() - 1)
        : new Date(start.getTime() + intervalMs - 1);

      for (const fl of featureLayers) {
        if (mode === 'timeExtent') {
          fl.timeExtent = new TimeExtent({
            start,
            end,
          });
        }
      }

      // Time definitionExpression is stored as a composable part so legend
      // class filters (e.g. Destroyed) can AND with the date window.
      if (mode === 'definitionExpression' && dateField) {
        const startSql = formatSqlDate(start);
        const endSql = formatSqlDate(end);
        setLayerDefinitionExpressionTimePart(
          layerId,
          `${dateField} >= DATE '${startSql}' AND ${dateField} <= DATE '${endSql}'`
        );
      } else {
        setLayerDefinitionExpressionTimePart(layerId, null);
      }

      setExternalLayerTimeFilter(layerId, {
        startIso: start.toISOString(),
        endIso: end.toISOString(),
        label: formatTimeFilterLabel(start),
      });
    },
    [
      dateField,
      layerId,
      getAddedExternalLayers,
      mode,
      intervalMs,
      setExternalLayerTimeFilter,
      setLayerDefinitionExpressionTimePart,
    ]
  );

  useEffect(() => {
    if (status !== 'ready' || steps.length === 0) return;
    applyStep(stepIndex, steps);
  }, [applyStep, status, stepIndex, steps]);

  // Keep the last applied window after the slider UI unmounts (accordion collapse)
  // so chat AI can still prefetch the visible slice. Clear only when discovery fails.
  useEffect(() => {
    if (status === 'unavailable') {
      setExternalLayerTimeFilter(layerId, null);
      setLayerDefinitionExpressionTimePart(layerId, null);
    }
  }, [
    status,
    layerId,
    setExternalLayerTimeFilter,
    setLayerDefinitionExpressionTimePart,
  ]);

  const setStepIndex = useCallback(
    (index: number) => {
      if (!steps.length) return;
      setStepIndexState(Math.max(0, Math.min(index, steps.length - 1)));
    },
    [steps.length]
  );

  const setStepFromDate = useCallback(
    (date: Date) => {
      if (!steps.length) return;
      setStepIndexState(nearestStepIndex(steps, date));
    },
    [steps]
  );

  const fullStart = steps[0];
  const fullEnd = steps.length > 0 ? steps[steps.length - 1] : undefined;

  return useMemo(
    () => ({
      steps,
      stepIndex,
      setStepIndex,
      setStepFromDate,
      fullStart,
      fullEnd,
      status,
      message,
    }),
    [
      steps,
      stepIndex,
      setStepIndex,
      setStepFromDate,
      fullStart,
      fullEnd,
      status,
      message,
    ]
  );
}
